import styled from '@emotion/styled';
import {
  getSpacing,
  colors,
  getBorderRadius,
  getFontStyle,
  fontWeights,
  fontSizes,
} from '@cabonline/ui-shared';
import { ButtonProps, ButtonVariant } from './button';

interface StyledProps extends ButtonProps {
  fullWidth?: boolean;
  width?: string;
}

const baseStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('xxs')} ${getSpacing('xs')};
  min-height: ${getSpacing('xl')};
  border-radius: ${getBorderRadius('m')};
  border: 1px solid transparent;
  @media (prefers-reduced-motion: no-preference) {
    transition: all 0.3s ease;
  }
  cursor: pointer;
  outline-color: ${colors.white};
  outline-offset: 2px;
  white-space: nowrap;
`;

const disabledStyle = `
  background-color: ${colors.gray3};
  cursor: not-allowed;
`;

const getBackgroundColor = (
  variant: ButtonVariant = 'primary',
  hovered = false,
  active = false
) => {
  if (variant === 'primary') {
    return hovered || active ? colors.primaryHover : colors.primary;
  } else if (variant === 'secondary') {
    return hovered || active ? colors.blackHover : colors.black;
  } else {
    return hovered || active ? colors.gray4 : colors.white;
  }
};

const getBorder = (variant: ButtonVariant = 'primary', active = false) => {
  if (variant === 'tertiary' && !active) {
    return `1px solid ${colors.gray2}`;
  }
  return '1px solid transparent';
};

export const StyledButton = styled.button<StyledProps>`
  ${baseStyles};
  ${getFontStyle('m', 'extraBold')};
  color: ${(props) => getButtonTextColor(props.variant, props.disabled)};
  background-color: ${(props) =>
    getBackgroundColor(props.variant, false, false)};
  border: ${(props) => getBorder(props.variant, false)};
  width: ${(props) => (props.fullWidth ? '100%' : props.width || 'auto')};
  outline-offset: ${(props) => (props.variant === 'tertiary' ? '0px' : '2px')};
  &:hover {
    background-color: ${(props) =>
      getBackgroundColor(props.variant, true, false)};
  }
  &:active {
    border: ${(props) => getBorder(props.variant, true)};
    background-color: ${(props) =>
      getBackgroundColor(props.variant, true, true)};
  }
  &:disabled {
    ${disabledStyle};
  }
  &[aria-disabled='true'] {
    ${disabledStyle};
    color: ${colors.gray1};
  }
  &:focus-visible {
    border: ${(props) => getBorder(props.variant, true)};
    outline: ${(props) =>
      `2px solid ${props.isDarkBackground ? colors.white : colors.black}`};
    transition: none;
  }
`;

const getButtonTextColor = (
  variant: ButtonVariant = 'primary',
  disabled = false
) => {
  if (disabled) return colors.gray1;

  if (variant === 'primary' || variant === 'tertiary') {
    return colors.black;
  }

  return colors.white;
};

export const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export const StyledLabelColumn = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.15;
`;

export const StyledSubLabel = styled.span`
  font-family: inherit;
  font-weight: ${fontWeights.semi};
  font-size: ${fontSizes.s}px;
  line-height: 1.2;
  color: inherit;
  opacity: 0.7;
  white-space: normal;
  text-align: center;
`;
